import React, { useEffect, useState, useMemo } from "react";
import { ClientNotify } from "@/components/ClientNotify";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Badge, Btn, Input, Textarea, Select, Field, Modal } from "@/components/ui-kit";
import { JOB_STATUSES, REPAIR_OUTCOMES, REPAIR_TYPES, findMeta, fmtDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { WarrantyAlert } from "@/pages/Inward";
import QRLabel from "@/components/QRLabel";
import GatepassDoc from "@/components/GatepassDoc";
import { PendingMedia, uploadQueued } from "@/components/MediaUploader";
import { Truck, ShieldCheck, QrCode, Layers, FileText } from "lucide-react";

const TypeBadge = ({ t }) => { const m = findMeta(REPAIR_TYPES, t || "new_repair"); return <Badge className={m.cls} data-testid="type-badge">{m.label}</Badge>; };

export default function Outward() {
  const { can } = useAuth();
  const [ready, setReady] = useState([]);
  const [dispatched, setDispatched] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [gatepasses, setGatepasses] = useState([]);
  const [target, setTarget] = useState(null);
  const [batchTarget, setBatchTarget] = useState(null);
  const [gatepass, setGatepass] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [label, setLabel] = useState(null);

  const load = () => {
    api.get("/jobs", { params: typeFilter ? { repair_type: typeFilter } : {} }).then((r) => {
      setAllJobs(r.data);
      setReady(r.data.filter((j) => ["qa_review", "ready_for_dispatch", "testing"].includes(j.status)));
      setDispatched(r.data.filter((j) => j.status === "dispatched"));
    });
    api.get("/gatepasses").then((r) => setGatepasses(r.data)).catch(() => {});
  };
  useEffect(load, [typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = params.get("dispatch");
    if (!id || !allJobs.length) return;
    const j = allJobs.find((x) => x.id === id);
    if (j && ["qa_review", "ready_for_dispatch", "testing"].includes(j.status)) setTarget(j);
    else toast.error("That job isn't ready for dispatch yet");
    params.delete("dispatch"); setParams(params, { replace: true });
  }, [allJobs, params, setParams]);

  // Group QA-passed units by batch; singles stay as-is
  const groups = useMemo(() => {
    const singles = []; const batches = {};
    for (const j of ready) {
      if (!j.batch_id) { singles.push(j); continue; }
      if (!batches[j.batch_id]) {
        const all = allJobs.filter((x) => x.batch_id === j.batch_id);
        batches[j.batch_id] = { batch_id: j.batch_id, batch_number: j.batch_number, ready: [], pending: all.filter((x) => !["dispatched", "qa_review", "ready_for_dispatch", "testing"].includes(x.status)).length };
      }
      batches[j.batch_id].ready.push(j);
    }
    Object.values(batches).forEach((b) => b.ready.sort((a, c) => a.batch_index - c.batch_index));
    return { singles, batches: Object.values(batches) };
  }, [ready, allJobs]);

  const openLabel = async (j) => {
    try { const { data } = await api.get(`/jobs/${j.id}`); if (data.outward) setLabel(outwardLabelProps(j, data.outward)); else toast.error("No outward record"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const canDispatch = can("job.dispatch");

  return (
    <div className="space-y-4" data-testid="outward-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl">Outward Dispatch</h1>
          <p className="text-sm text-muted-foreground">QA-passed equipment ready for gatepass & delivery note generation.</p>
        </div>
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-44" data-testid="outward-type-filter">
          <option value="">All Repair Types</option>
          {REPAIR_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
      </div>

      <Card className="p-4">
        <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide"><ShieldCheck size={15} /> Awaiting Dispatch ({ready.length})</h3>
        {ready.length === 0 ? <p className="text-sm text-muted-foreground">No jobs awaiting dispatch.</p> : (
          <div className="space-y-2">
            {groups.batches.map((b) => (
              <div key={b.batch_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2" data-testid={`batch-ready-${b.batch_id}`}>
                <div>
                  <div className="flex items-center gap-2 font-semibold"><Layers size={14} className="text-primary" /> {b.ready[0].equipment_name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{b.batch_number} · <b className="text-foreground">{b.ready.length}</b> units QA-passed{b.pending > 0 && <> · <span className="text-amber-500">{b.pending} still in workshop</span></>}</div>
                </div>
                <div className="flex items-center gap-2">
                  <TypeBadge t={b.ready[0].repair_type} />
                  {canDispatch ? (
                    <Btn size="sm" onClick={() => setBatchTarget(b)} data-testid={`dispatch-batch-${b.batch_id}`}><Truck size={14} /> Dispatch Batch ({b.ready.length})</Btn>
                  ) : <span className="font-mono text-[10px] text-muted-foreground">Supervisor approval required</span>}
                </div>
              </div>
            ))}
            {groups.singles.map((j) => {
              const st = findMeta(JOB_STATUSES, j.status);
              return (
                <div key={j.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
                  <div>
                    <div className="font-semibold">{j.equipment_name}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{j.job_number} · SN {j.serial_number}{j.batch_number && <> · {j.batch_number}</>}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <TypeBadge t={j.repair_type} />
                    <WarrantyAlert job={j} compact />
                    <Badge className={st.cls}>{st.label}</Badge>
                    {canDispatch ? (
                      <Btn size="sm" onClick={() => setTarget(j)} data-testid={`dispatch-${j.id}`}><Truck size={14} /> Dispatch</Btn>
                    ) : <span className="font-mono text-[10px] text-muted-foreground">Supervisor approval required</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide"><FileText size={15} /> Batch Gatepasses ({gatepasses.length})</h3>
        {gatepasses.length === 0 ? <p className="text-sm text-muted-foreground">No batch gatepasses yet.</p> : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {gatepasses.map((g) => (
              <button key={g.id} type="button" onClick={() => setGatepass(g)} data-testid={`gatepass-${g.id}`}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-left hover:bg-accent/50">
                <div>
                  <div className="font-mono text-xs font-bold text-primary">{g.gatepass_number}</div>
                  <div className="text-sm">{g.equipment_name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{g.batch_number} · {g.quantity} units · {fmtDate(g.dispatch_date)}</div>
                </div>
                <FileText size={16} className="shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide">Dispatched ({dispatched.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="border-b border-border text-left font-mono text-[10px] uppercase text-muted-foreground">
              <th className="px-3 py-2">Job #</th><th className="px-3 py-2">Equipment</th><th className="px-3 py-2">Serial</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Gatepass</th><th className="px-3 py-2">Received</th><th className="px-3 py-2 text-right">Label</th>
            </tr></thead>
            <tbody>
              {dispatched.map((j) => (
                <tr key={j.id} className="border-b border-border/60">
                  <td className="px-3 py-2 font-mono text-xs text-primary">{j.job_number}</td>
                  <td className="px-3 py-2">{j.equipment_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{j.serial_number}</td>
                  <td className="px-3 py-2"><TypeBadge t={j.repair_type} /></td>
                  <td className="px-3 py-2 font-mono text-[11px]">{j.gatepass_number || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">{fmtDate(j.received_at)}</td>
                  <td className="px-3 py-2 text-right"><button title="Outward label" data-testid={`outward-label-${j.id}`} onClick={() => openLabel(j)} className="rounded border border-border p-1 hover:bg-accent"><QrCode size={14} /></button></td>
                </tr>
              ))}
              {dispatched.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Nothing dispatched yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {target && <DispatchForm job={target} onClose={() => setTarget(null)} onSaved={(ow) => { const j = target; setTarget(null); load(); if (ow) setLabel(outwardLabelProps(j, ow)); }} />}
      {label && <QRLabel open onClose={() => setLabel(null)} {...label} />}
      {batchTarget && <BatchDispatchForm batch={batchTarget} onClose={() => setBatchTarget(null)} onSaved={(gp) => { setBatchTarget(null); load(); setGatepass(gp); }} />}
      {gatepass && <GatepassDoc gp={gatepass} onClose={() => setGatepass(null)} />}
    </div>
  );
}

function addMonths(d, m) { const x = new Date(d); x.setMonth(x.getMonth() + Number(m || 0)); return x; }

export const outwardLabelProps = (job, ow) => ({
  heading: `Outward Label · ${job.job_number}`, title: job.equipment_name, code: job.job_number, badge: "Outward",
  stamp: `Dispatched ${fmtDate(ow.dispatch_date)}`,
  lines: [["SERIAL", job.serial_number], ["OUTCOME", (findMeta(REPAIR_OUTCOMES, ow.repair_outcome)?.label) || ow.repair_outcome],
    ["WARRANTY", ow.warranty_provided ? (ow.warranty_period || "Yes") : "None"],
    ["VALID TILL", ow.warranty_provided && ow.warranty_until ? fmtDate(ow.warranty_until) : "—"]],
});

function DispatchFields({ f, setF, media, setMedia, clientEmail }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const until = addMonths(new Date(), f.warranty_period_months);
  return (
    <>
      <div>
        <div className="mb-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Dispatch Photos / Video (optional · packed condition, seals)</div>
        <PendingMedia files={media} onChange={setMedia} />
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" data-testid="f-warranty-provided" checked={f.warranty_provided} onChange={(e) => setF({ ...f, warranty_provided: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--primary))]" />
        <span className="font-medium">Provide repair warranty</span> <span className="text-xs text-muted-foreground">(optional · starts today at dispatch)</span>
      </label>
      {f.warranty_provided && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Warranty Period (months)">
            <div className="flex gap-1">
              {[3, 6, 12].map((m) => (
                <button key={m} type="button" data-testid={`warranty-preset-${m}`} onClick={() => setF({ ...f, warranty_period_months: m })}
                  className={`rounded-md border px-2.5 py-1.5 text-xs font-bold ${Number(f.warranty_period_months) === m ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{m}m</button>
              ))}
              <Input type="number" min={1} max={120} data-testid="f-warranty-months" value={f.warranty_period_months} onChange={set("warranty_period_months")} className="w-20" />
            </div>
          </Field>
          <Field label="Warranty Valid Until">
            <div data-testid="f-warranty-until" className="flex h-9 items-center rounded-md border border-border bg-accent/40 px-3 font-mono text-sm">{fmtDate(until.toISOString())}</div>
          </Field>
        </div>
      )}
      <Field label="Repair Outcome" required>
        <Select value={f.repair_outcome} onChange={set("repair_outcome")} data-testid="outcome-select">
          {REPAIR_OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      </Field>
      <Field label="Courier / Delivery Details"><Input value={f.courier_details} onChange={set("courier_details")} placeholder="Courier, AWB, vehicle no." /></Field>
      <Field label="Client Sign-off Name"><Input value={f.client_signoff_name} onChange={set("client_signoff_name")} /></Field>
      <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set("notes")} /></Field>
      <ClientNotify checked={f.notify_client} onChange={(v) => setF({ ...f, notify_client: v })} email={clientEmail} what="dispatched" />
    </>
  );
}

const DEFAULT_DISPATCH = { notify_client: true, repair_outcome: "repaired", courier_details: "", client_signoff_name: "", notes: "", warranty_provided: false, warranty_period_months: 6 };

function DispatchForm({ job, onClose, onSaved }) {
  const [f, setF] = useState(DEFAULT_DISPATCH);
  const [media, setMedia] = useState([]);
  const save = async () => {
    try {
      const { data } = await api.post("/outward", { job_id: job.id, ...f, warranty_period_months: Number(f.warranty_period_months) || 0 });
      toast.success(`${job.job_number} dispatched`);
      await uploadQueued(media, { entityType: "job", entityId: job.id, stage: "outward" }, "dispatch photos");
      onSaved(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const rt = findMeta(REPAIR_TYPES, job.repair_type || "new_repair");
  return (
    <Modal open onClose={onClose} title={`Outward Gatepass · ${job.job_number}`}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-accent/40 p-3 text-sm">
          <div><b>{job.equipment_name}</b> <span className="font-mono text-xs text-muted-foreground">SN {job.serial_number}</span>
            {job.repair_type === "warranty_repair" && (job.warranty_ref || job.supply_date) && (
              <div className="font-mono text-[11px] text-muted-foreground">Warranty ref {job.warranty_ref || "—"} · Supplied {job.supply_date ? fmtDate(job.supply_date) : "—"}</div>
            )}
          </div>
          <Badge className={rt.cls}>{rt.label}</Badge>
        </div>
        <DispatchFields f={f} setF={setF} media={media} setMedia={setMedia} clientEmail={job.client_email} />
        <div className="flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="dispatch-submit-btn"><Truck size={14} /> Confirm Dispatch</Btn></div>
      </div>
    </Modal>
  );
}

function BatchDispatchForm({ batch, onClose, onSaved }) {
  const [f, setF] = useState(DEFAULT_DISPATCH);
  const [selected, setSelected] = useState(() => new Set(batch.ready.map((j) => j.id)));
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => {
    if (!selected.size) { toast.error("Select at least one unit"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/outward/batch", { batch_id: batch.batch_id, job_ids: [...selected], ...f, warranty_period_months: Number(f.warranty_period_months) || 0 });
      toast.success(`${data.gatepass_number}: ${data.quantity} units dispatched`);
      await uploadQueued(media, { entityType: "gatepass", entityId: data.id, stage: "outward" }, "dispatch photos");
      onSaved(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const j0 = batch.ready[0];
  return (
    <Modal open onClose={onClose} title={`Batch Gatepass · ${batch.batch_number}`} wide>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-accent/40 p-3 text-sm">
          <div><b>{j0.equipment_name}</b> <span className="font-mono text-xs text-muted-foreground">{j0.brand} {j0.model_number}</span>
            <div className="font-mono text-[11px] text-muted-foreground">{batch.ready.length} QA-passed of {j0.batch_quantity} units{batch.pending > 0 && <> · <span className="text-amber-500">{batch.pending} still in workshop (not included)</span></>}</div>
          </div>
          <TypeBadge t={j0.repair_type} />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <span>Units on this gatepass ({selected.size}/{batch.ready.length})</span>
            <div className="flex gap-2">
              <button type="button" className="hover:text-foreground" data-testid="batch-select-all" onClick={() => setSelected(new Set(batch.ready.map((j) => j.id)))}>All</button>
              <button type="button" className="hover:text-foreground" data-testid="batch-select-none" onClick={() => setSelected(new Set())}>None</button>
            </div>
          </div>
          <div className="grid max-h-44 gap-1 overflow-y-auto rounded-md border border-border p-2 sm:grid-cols-2 lg:grid-cols-3">
            {batch.ready.map((j) => (
              <label key={j.id} className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 font-mono text-[11px] ${selected.has(j.id) ? "bg-primary/10" : "hover:bg-accent"}`}>
                <input type="checkbox" data-testid={`batch-unit-${j.id}`} checked={selected.has(j.id)} onChange={() => toggle(j.id)} className="h-3.5 w-3.5 accent-[hsl(var(--primary))]" />
                <span className="text-muted-foreground">#{j.batch_index}</span> <span className="truncate font-semibold">{j.serial_number}</span>
              </label>
            ))}
          </div>
        </div>
        <DispatchFields f={f} setF={setF} media={media} setMedia={setMedia} clientEmail={j0.client_email} />
        <div className="flex justify-end gap-2">
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={busy} data-testid="batch-dispatch-submit-btn"><Truck size={14} /> {busy ? "Dispatching…" : `Dispatch ${selected.size} Units`}</Btn>
        </div>
      </div>
    </Modal>
  );
}
